import { useMemo, useState } from 'react'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { styled, alpha } from '@mui/material/styles'
import {
  Slide,
  InputAdornment,
  ClickAwayListener,
  Button,
  IconButton,
  Autocomplete,
  FormControl,
  TextField,
  Typography,
} from '@mui/material'
import { useStore, useGetList, useTranslate } from 'react-admin'
import { BranchSelect } from './type'
import { COMMON_FIELDS } from '../../providers/localization/constant'

const APPBAR_MOBILE = 64
const APPBAR_DESKTOP = 92

const SearchbarStyle = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: theme.zIndex.appBar,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: APPBAR_MOBILE,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  padding: theme.spacing(0, 3),
  backgroundColor: `${alpha(theme.palette.background.default, 0.72)}`,
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}))

export default function Searchbar(): JSX.Element {
  const translate = useTranslate()
  const [isOpen, setOpen] = useState(false)
  const [selectBranch, setSelectBranch] = useStore<BranchSelect | null>('branch.selected', null)
  const [inputValue, setInputValue] = useState('')

  const handleOpen = (): void => {
    setOpen((prev) => !prev)
  }
  const { data } = useGetList('branches', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'branch_code', order: 'ASC' },
    meta: { isPaginate: false },
  })

  const branches: BranchSelect[] = useMemo(() => {
    const transformProperties = data
      ? data.map((b) => ({
          id: b.id,
          title: `${b.branch_code} ${b.branch_name_th || '-'} / ${b.branch_name_en || '-'}`,
        }))
      : []
    if (transformProperties.length > 1) {
      transformProperties.unshift({
        id: null,
        title: translate(`${COMMON_FIELDS}.published_to.choices.all_branches`),
      })
    }
    if (data && selectBranch == null) {
      setSelectBranch(transformProperties[0])
    }
    return transformProperties
  }, [data, selectBranch, setSelectBranch, translate])
  return (
    <ClickAwayListener
      onClickAway={(): void => {
        setOpen(false)
      }}
      mouseEvent='onMouseUp'
    >
      <div>
        {!isOpen && (
          <IconButton onClick={handleOpen} sx={{ borderRadius: 3 }}>
            <SwapVertIcon sx={{ color: 'text.disabled', width: 20, height: 20 }} />
            <Typography variant='h4'>
              {selectBranch != null
                ? selectBranch.title
                : translate('component.searchBar.placeholder')}
            </Typography>
          </IconButton>
        )}
        <Slide direction='down' in={isOpen} mountOnEnter unmountOnExit>
          <SearchbarStyle>
            <InputAdornment position='start'>
              <SwapVertIcon sx={{ color: 'text.disabled', width: 20, height: 20 }} />
            </InputAdornment>
            <FormControl fullWidth sx={{ mr: 1, fontWeight: 'fontWeightBold' }}>
              <Autocomplete
                value={selectBranch}
                onChange={(event, newValue: BranchSelect): void => {
                  setSelectBranch(newValue)
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue): void => {
                  setInputValue(newInputValue)
                }}
                getOptionLabel={(option: BranchSelect): string => option.title}
                id='autocompleteBranches'
                options={branches}
                isOptionEqualToValue={(opt, val): boolean => opt.id === val.id}
                renderInput={(params): JSX.Element => (
                  <TextField {...params} label={translate('component.searchBar.placeholder')} />
                )}
                disableClearable
              />
            </FormControl>
            <Button variant='contained' onClick={handleOpen}>
              Search
            </Button>
          </SearchbarStyle>
        </Slide>
      </div>
    </ClickAwayListener>
  )
}
